/**
 * /photo-album — standalone guest photo album: landing page + creation.
 *
 * The whole flow is: name the event, pick its date, sign in, pay. Everything
 * else about the album is set up afterwards on the manage screen, because
 * asking for privacy settings and moderation preferences before someone has
 * paid is asking them to make decisions about a thing they have not seen.
 *
 * Auth is the SHARED modal (window.authModal), never a bespoke form — see the
 * comment in photo-album.html for why that matters.
 *
 * Two server-side gates surface here as messages rather than dead ends:
 *   - a verified email is required (403)
 *   - a cumulative cap on albums per account (429)
 * Both tell the visitor what to do next, per the house rule that a refusal
 * should never be a wall with nothing behind it.
 */

(function () {
    'use strict';

    // Localized strings. The English page ships none and falls through to these
    // defaults; each localized page emits window.__PA_I18N ahead of this script
    // (see scripts/build-photo-album-locales.js), so there is exactly one copy
    // of this file rather than one per language.
    const T = Object.assign({
        errName: 'Whose event is it? A name or two is enough.',
        errDate: 'Pick the date — uploads stay open for 90 days after it.',
        settingUp: 'Setting up your album…',
        toPayment: 'Taking you to payment…',
        created: 'Album created. Opening it now…',
        generic: 'Something went wrong. Please try again.',
        createFail: 'Could not create the album. Please try again.',
        signIn: 'Please sign in to continue.',
        // Label only — the price lives in its own [data-price-usd] span so
        // the currency manager owns it. Putting it back in this string would
        // overwrite the converted price the moment auth settles.
        btnReady: 'Continue to payment'
    }, window.__PA_I18N || {});

    const $ = (id) => document.getElementById(id);
    const els = {
        title: $('albumTitle'),
        date: $('albumDate'),
        btn: $('createBtn'),
        status: $('createStatus'),
        eventNotice: $('eventNotice'),
        proceedAnyway: $('proceedAnyway')
    };
    if (!els.btn) return;

    // An event in the past is almost always a typo at this step, but not always
    // — someone may be collecting photos after the fact, which is a perfectly
    // good reason to buy this. So it is allowed, just not the default.
    //
    // The forward bound is the one that matters: every album clock is anchored
    // to this date, so a far-future date would mean indefinite hosting for a
    // one-time payment. Mirrors MAX_EVENT_MONTHS_AHEAD/BEHIND in
    // albums.service.js, which is where it is actually enforced — this only
    // saves the visitor a round-trip.
    // Local days: toISOString() of a local midnight is the day before east
    // of Greenwich.
    const today = new Date();
    const localYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    els.date.min = localYmd(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()));
    els.date.max = localYmd(new Date(today.getFullYear(), today.getMonth() + 18, today.getDate()));

    function say(msg, isError) {
        els.status.textContent = msg || '';
        els.status.className = 'builder-status' + (isError ? ' error' : '');
    }

    function currentUser() {
        try { return firebase.auth().currentUser; } catch { return null; }
    }

    /** Resolve once auth has actually settled, rather than reading it too early. */
    function whenAuthReady() {
        return new Promise((resolve) => {
            try {
                const unsub = firebase.auth().onAuthStateChanged((user) => {
                    unsub();
                    resolve(user);
                });
            } catch {
                resolve(null);
            }
        });
    }

    /**
     * Does this account already run an event here? If so their album is free,
     * and a standalone purchase is money for something they already have.
     *
     * One `.limit(1)` read, cached for the page. The owner query is the same
     * one my-projects.js uses and is authorized by firestore.rules; a failure
     * resolves to false, because a read hiccup must never block a real sale.
     */
    let ownsEvent = null;
    async function hasEvent(user) {
        if (ownsEvent !== null) return ownsEvent;
        try {
            const snap = await firebase.firestore()
                .collection('rsvp_forms')
                .where('owner', '==', user.uid)
                .limit(1)
                .get();
            ownsEvent = !snap.empty;
        } catch (err) {
            console.warn('[photo-album] could not check for existing events:', err && err.message);
            ownsEvent = false;
        }
        return ownsEvent;
    }

    // Set once the visitor has said "this is a different event". Deliberately
    // NOT persisted: it is a decision about this purchase, not a preference.
    let acknowledgedDuplicate = false;

    function validate() {
        const title = els.title.value.trim();
        const date = els.date.value;
        if (title.length < 2) {
            say(T.errName, true);
            els.title.focus();
            return null;
        }
        if (!date) {
            say(T.errDate, true);
            els.date.focus();
            return null;
        }
        return { title, eventDate: date };
    }

    async function createAndPay() {
        const input = validate();
        if (!input) return;

        els.btn.disabled = true;
        say(T.settingUp);

        let user = currentUser() || await whenAuthReady();
        if (!user) {
            // Hand off to the shared modal, then pick up exactly where we left
            // off — the visitor should not have to re-type what they just typed.
            say('');
            els.btn.disabled = false;
            try {
                window.authModal.show('creating your photo album');
                const unsub = firebase.auth().onAuthStateChanged((u) => {
                    if (u) { unsub(); createAndPay(); }
                });
            } catch {
                say(T.signIn, true);
            }
            return;
        }

        // The gate lives HERE, at the click that costs money, rather than as a
        // redirect on page load — see the note in photo-album.html.
        if (!acknowledgedDuplicate && await hasEvent(user)) {
            els.btn.disabled = false;
            say('');
            if (els.eventNotice) {
                els.eventNotice.style.display = '';
                els.eventNotice.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
            return;
        }

        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/albums/standalone', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(input)
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok || !data.success) {
                // 403 = email not verified, 429 = cumulative cap. Both come back
                // with copy that says what to do, so show it as written.
                say(data.error || T.createFail, true);
                els.btn.disabled = false;
                return;
            }

            say(T.toPayment);
            const checkout = await fetch(`/api/albums/${data.album.id}/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                // So a Spanish buyer returns to /es/album-manage, not the
                // English one. Derived from the path this page is served at.
                body: JSON.stringify({
                    origin: location.origin,
                    locale: (location.pathname.match(/^\/(es|fr|de|pt|he|ar)\//) || [])[1] || null
                })
            });
            const pay = await checkout.json().catch(() => ({}));
            if (pay.url) {
                location.href = pay.url;
                return;
            }
            // The album exists even if checkout could not start, so send them to
            // manage rather than stranding them — they can pay from there.
            say(T.created);
            location.href = `/album-manage?id=${encodeURIComponent(data.album.id)}`;
        } catch (err) {
            console.error('[photo-album] create failed:', err);
            say(T.generic, true);
            els.btn.disabled = false;
        }
    }

    els.btn.addEventListener('click', createAndPay);
    if (els.proceedAnyway) {
        els.proceedAnyway.addEventListener('click', () => {
            acknowledgedDuplicate = true;
            els.eventNotice.style.display = 'none';
            createAndPay();
        });
    }
    els.date.addEventListener('keydown', (e) => { if (e.key === 'Enter') createAndPay(); });
    els.title.addEventListener('keydown', (e) => { if (e.key === 'Enter') els.date.focus(); });

    // Someone already signed in who lands here mid-flow gets the friendlier
    // button copy rather than a generic one, and a way back to what they own.
    whenAuthReady().then((user) => {
        if (!user) return;
        const label = $('createBtnLabel');
        if (label) label.textContent = T.btnReady;
        else els.btn.textContent = T.btnReady;
        const mine = $('myAlbumsLink');
        if (mine) mine.style.display = '';
        // Surface the notice up front too, so the cheaper option is visible
        // before they fill the form in — not only after they commit to paying.
        hasEvent(user).then((yes) => {
            if (yes && els.eventNotice) els.eventNotice.style.display = '';
        });
    });
})();
